'use client';

import Link from 'next/link';
import { useState, useEffect, useRef, useCallback } from 'react';
import { LucideIcon } from '@/components/LucideIcon';
import type { NavItem, CtaButton } from '@/lib/nav';

type HeaderClientProps = {
  headerItems: NavItem[];
  ctaButtons: CtaButton[];
  /** Whether the page opens with a dark hero. When false, the header is always
   *  solid/readable (for light-top pages like release notes or policies). */
  transparent?: boolean;
};

/**
 * Classes for one of the two cross-fading header logos.
 *
 * @param visible true for the logo currently shown; false for the one faded out.
 *
 * The hidden variant is `absolute` (so it takes no space) but MUST be anchored
 * with top-0/left-0 and made pointer-events-none — see the comment at the logo
 * markup for the breadcrumb-swallowing bug that came from omitting both.
 */
export function logoImgClass(visible: boolean): string {
  return visible
    ? 'h-14 w-auto transition-opacity duration-300 opacity-100'
    : 'h-14 w-auto transition-opacity duration-300 opacity-0 absolute top-0 left-0 pointer-events-none';
}

/* ─── Component ─── */

export default function HeaderClient({ headerItems, ctaButtons, transparent = true }: HeaderClientProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openDropdown = useCallback((key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(key);
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 150);
  }, []);

  // Header shows its solid (readable) skin when scrolled, OR when the page has
  // no dark hero behind it (transparent=false). Otherwise white-on-transparent.
  const solid = scrolled || !transparent;

  const navLinkClass = solid
    ? 'text-t-body hover:text-t-heading'
    : 'text-white/80 hover:text-white';

  const chevron = (open: boolean) => (
    <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-stone-200/50'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* ── Logo ──
              Two stacked images cross-fade: white over a dark hero, black once
              the header is solid. The hidden one is taken out of flow so the
              header doesn't reserve space for both.

              It MUST be anchored (top-0 left-0). An absolutely-positioned box
              with auto offsets resolves to its STATIC position, and since the
              line box is already full that dropped the invisible logo onto a
              SECOND LINE — directly over the breadcrumb trail. opacity:0 still
              hit-tests, and this sits inside <Link href="/">, so every click on
              the "Products" crumb silently went to the homepage instead.
              pointer-events-none is the belt-and-braces half: a logo nobody can
              see must never be able to swallow a click. */}
          <Link href="/" className="relative shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/OfficePortal-white.svg"
              alt="OfficePortal"
              width={200}
              height={61}
              className={logoImgClass(!solid)}
              aria-hidden={solid ? 'true' : undefined}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/OfficePortal-black.svg"
              alt="OfficePortal"
              width={200}
              height={61}
              className={logoImgClass(solid)}
              aria-hidden={solid ? undefined : 'true'}
            />
          </Link>

          {/* ── Desktop Nav ── */}
          <div className="hidden lg:flex items-center gap-0.5">
            {headerItems.map((item, idx) => {
              const key = `${item.label}-${idx}`;
              const isOpen = activeDropdown === key;

              // Plain link
              if (item.type === 'link' || !item.columns?.length) {
                return (
                  <Link
                    key={key}
                    href={item.url || '#'}
                    target={item.openInNewTab ? '_blank' : undefined}
                    rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${navLinkClass}`}
                  >
                    {item.label}
                  </Link>
                );
              }

              const columns = item.columns ?? [];
              const colCount = Math.max(1, columns.length);
              const isMega = item.type === 'mega';
              const hasDescriptions = columns.some((col) =>
                (col.links ?? []).some((l) => l.description),
              );
              const colWidth = hasDescriptions ? 300 : 210;
              const panelWidth = isMega
                ? `min(${colCount * colWidth + 48}px, calc(100vw - 32px))`
                : '288px';

              return (
                <div
                  key={key}
                  className="relative"
                  onMouseEnter={() => openDropdown(key)}
                  onMouseLeave={scheduleClose}
                >
                  <button className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${navLinkClass}`}>
                    {item.label}
                    {chevron(isOpen)}
                  </button>

                  <div
                    className={`absolute top-full -left-4 pt-4 transition-all duration-200 ${
                      isOpen
                        ? 'opacity-100 translate-y-0 pointer-events-auto'
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div
                      className="bg-white rounded-2xl shadow-2xl shadow-black/8 border border-stone-200/60 overflow-hidden"
                      style={{ width: panelWidth }}
                    >
                      <div className="h-1 w-full" style={{ background: 'var(--t-gradient, var(--color-t-primary))' }} />
                      <div className="p-6">
                        <div
                          className="grid gap-8"
                          style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
                        >
                          {columns.map((col, ci) => (
                            <div key={ci}>
                              {col.heading && (
                                <div
                                  className="flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-wider"
                                  style={{ color: 'var(--color-t-primary)' }}
                                >
                                  {col.heading}
                                </div>
                              )}
                              <div className="space-y-1">
                                {(col.links ?? []).map((link, li) => (
                                  <Link
                                    key={li}
                                    href={link.url || '#'}
                                    target={link.openInNewTab ? '_blank' : undefined}
                                    rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                                    className="flex items-start gap-3 px-3 py-2.5 -mx-1 rounded-xl hover:bg-stone-50 group transition-colors"
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    {link.icon && (
                                      <span className="mt-0.5 text-t-primary shrink-0" style={{ color: 'var(--color-t-primary)' }}>
                                        <LucideIcon name={link.icon} size={18} />
                                      </span>
                                    )}
                                    <span className="min-w-0">
                                      <span className="text-sm font-semibold text-stone-800 group-hover:text-t-primary transition-colors block leading-tight line-clamp-2">
                                        {link.label}
                                      </span>
                                      {link.description && (
                                        <span className="text-xs text-stone-400 group-hover:text-stone-500 transition-colors leading-snug block mt-0.5 line-clamp-2 min-h-[2rem]">
                                          {link.description}
                                        </span>
                                      )}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {isMega && item.feature?.enabled && (
                          <div
                            className="mt-6 rounded-2xl p-5 flex items-center justify-between gap-5 text-white"
                            style={{ background: 'linear-gradient(135deg, var(--color-t-primary), var(--color-t-primary-dark, var(--color-t-primary)))' }}
                          >
                            <div>
                              {item.feature.title && (
                                <strong className="block text-base mb-1">{item.feature.title}</strong>
                              )}
                              {item.feature.text && (
                                <p className="m-0 text-sm text-white/80">{item.feature.text}</p>
                              )}
                            </div>
                            {item.feature.buttonLabel && (
                              <Link
                                href={item.feature.buttonUrl || '#'}
                                onClick={() => setActiveDropdown(null)}
                                className="shrink-0 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap text-t-cta-text bg-t-cta hover:bg-t-cta-hover transition-colors"
                              >
                                {item.feature.buttonLabel}
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Desktop CTA ── */}
          <div className="hidden lg:flex items-center gap-3">
            {ctaButtons.map((btn, i) =>
              btn.style === 'primary' ? (
                <Link
                  key={i}
                  href={btn.url || '#'}
                  target={btn.openInNewTab ? '_blank' : undefined}
                  rel={btn.openInNewTab ? 'noopener noreferrer' : undefined}
                  className="px-5 py-2.5 text-sm font-semibold text-t-cta-text bg-t-cta rounded-lg hover:bg-t-cta-hover transition-colors"
                >
                  {btn.label}
                </Link>
              ) : (
                <Link
                  key={i}
                  href={btn.url || '#'}
                  target={btn.openInNewTab ? '_blank' : undefined}
                  rel={btn.openInNewTab ? 'noopener noreferrer' : undefined}
                  className={`px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                    solid
                      ? 'border-stone-300 text-t-heading hover:bg-stone-50'
                      : 'border-white/30 text-white hover:bg-white/10'
                  }`}
                >
                  {btn.label}
                </Link>
              ),
            )}
          </div>

          {/* ── Mobile Menu Button ── */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              solid ? 'hover:bg-stone-100 text-t-heading' : 'hover:bg-white/10 text-white'
            }`}
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileSubmenu(null); }}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* ── Mobile Menu ── */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-3 bg-white rounded-2xl mt-2 shadow-2xl border border-stone-100 max-h-[80vh] overflow-y-auto">
            <div className="px-2 space-y-0.5">
              {headerItems.map((item, idx) => {
                const key = `m-${item.label}-${idx}`;

                if (item.type === 'link' || !item.columns?.length) {
                  return (
                    <Link
                      key={key}
                      href={item.url || '#'}
                      target={item.openInNewTab ? '_blank' : undefined}
                      rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                      className="block px-4 py-3 text-sm font-semibold text-stone-800 hover:bg-stone-50 rounded-xl"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                }

                const open = mobileSubmenu === key;
                return (
                  <div key={key}>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-stone-800 hover:bg-stone-50 rounded-xl"
                      onClick={() => setMobileSubmenu(open ? null : key)}
                    >
                      {item.label}
                      <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {open && (
                      <div className="pb-2">
                        {(item.columns ?? []).map((col, ci) => (
                          <div key={ci} className="mb-2">
                            {col.heading && (
                              <div className="px-5 py-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-t-primary)' }}>
                                {col.heading}
                              </div>
                            )}
                            {(col.links ?? []).map((link, li) => (
                              <Link
                                key={li}
                                href={link.url || '#'}
                                target={link.openInNewTab ? '_blank' : undefined}
                                rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                                className="flex items-center gap-2 px-5 py-2 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-lg mx-2"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {link.icon && <LucideIcon name={link.icon} size={16} />}
                                {link.label}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {ctaButtons.length > 0 && <div className="border-t border-stone-100 mx-3 my-2" />}

              <div className="px-3 space-y-2 pb-2">
                {ctaButtons.map((btn, i) => (
                  <Link
                    key={i}
                    href={btn.url || '#'}
                    target={btn.openInNewTab ? '_blank' : undefined}
                    rel={btn.openInNewTab ? 'noopener noreferrer' : undefined}
                    className={`block w-full text-center px-5 py-3 text-sm font-semibold rounded-xl transition-colors ${
                      btn.style === 'primary'
                        ? 'text-t-cta-text bg-t-cta hover:bg-t-cta-hover'
                        : 'text-stone-800 border border-stone-200 hover:bg-stone-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {btn.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
